import React, { type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { CheckCircleFilled } from '@/components/Icons/CheckCircleFilled';
import { Close } from '@/components/Icons/Close';
import { Modal } from '@/components/Modal';
import { DisciplineCard } from '@/components/PreschoolEvaluationModal/DisciplineCard';
import { StudentDetailsCard } from '@/components/PreschoolEvaluationModal/StudentDetailsCard';
import { usePreschoolEvaluation } from '@/components/PreschoolEvaluationModal/hooks/usePreschoolEvaluation';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useScrollShadows } from '@/hooks/useScrollShadows';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { ButtonVariant, Size } from '@/types/components';

const TITLE_SWAP_PROGRESS = 0.5;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApproveFailure: () => void;
}

export const PreschoolEvaluationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onApproveFailure,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { progress, handleScroll } = useScrollFadeProgress();
  const {
    scrollRef,
    topProgress,
    bottomProgress,
    handleScroll: handleShadowScroll,
  } = useScrollShadows();

  const {
    evaluation,
    isLoading,
    isError,
    isApproved,
    isApproving,
    hasJustApproved,
    approve,
  } = usePreschoolEvaluation({ isOpen, onApproveFailure });

  const title = t('preschoolEvaluation.title');
  const approveLabel = t('preschoolEvaluation.approve');
  const emptyLabel = isError
    ? t('preschoolEvaluation.errorLoading')
    : t('preschoolEvaluation.emptyState');
  const isTitleInHeader = progress > TITLE_SWAP_PROGRESS;
  const showApproveButton = !!evaluation && !isApproved;
  const showApprovedNotice = !!evaluation && hasJustApproved;
  const showApprovedChip = !!evaluation && isApproved && !hasJustApproved;
  const hasFooter = showApproveButton || showApprovedNotice;

  const handleMobileScroll = (event: UIEvent<HTMLDivElement>) => {
    handleScroll(event);
    handleShadowScroll(event);
  };

  const mobileTitle = (
    <h2
      className={`text-title-36 text-foreground transition-opacity duration-75 md:hidden ${isTitleInHeader ? 'opacity-0' : 'opacity-100'}`}
    >
      {title}
    </h2>
  );

  const content = evaluation && (
    <div className="flex flex-col gap-5 px-4 pt-2 pb-5 md:gap-4 md:px-5 md:pt-5">
      {mobileTitle}

      <div className="flex flex-col gap-3">
        {showApprovedChip && (
          <span className="text-caption-12 inline-flex w-fit items-center gap-1 rounded-xl border border-transparent bg-success-surface py-[2px] pr-2 pl-1 text-foreground contrast:border-border">
            <CheckCircleFilled className="size-4 shrink-0" />
            {approveLabel}
          </span>
        )}

        <StudentDetailsCard evaluation={evaluation} />
      </div>

      <div className="flex flex-col gap-3 md:gap-4">
        {evaluation.disciplines.map((discipline) => (
          <DisciplineCard
            key={discipline.orderNumber}
            discipline={discipline}
          />
        ))}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      label={title}
      className="md:h-[788px] md:max-h-[85svh] md:w-[min(853px,calc(100vw-40px))]"
      onClose={onClose}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center gap-3 px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:px-5 md:pt-5 md:pb-4">
          <button
            type="button"
            aria-label={t('global.back')}
            onClick={onClose}
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
          >
            <ArrowLeft />
          </button>

          <h2
            className={`text-callout-16 min-w-0 flex-1 truncate text-center text-foreground transition-opacity duration-75 md:hidden ${isTitleInHeader ? 'opacity-100' : 'opacity-0'}`}
          >
            {title}
          </h2>

          <span className="size-6 shrink-0 md:hidden" />

          <h2 className="text-headline-20 hidden flex-1 text-foreground md:block">
            {title}
          </h2>

          <button
            type="button"
            aria-label={t('global.close')}
            onClick={onClose}
            className="hidden size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
          >
            <Close />
          </button>
        </header>

        {isLoading && (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="px-4 pt-2">{mobileTitle}</div>
            <div className="flex flex-1 items-center justify-center">
              <PageSpinner className="size-[22px] text-subtle-foreground" />
            </div>
          </div>
        )}

        {!isLoading && !evaluation && (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="px-4 pt-2">{mobileTitle}</div>
            <EmptyState label={emptyLabel} />
          </div>
        )}

        {!isLoading && isMobile && (
          <div className="relative flex min-h-0 flex-1 flex-col">
            <div
              style={{ opacity: topProgress }}
              className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-constant-black/6 to-transparent transition-opacity duration-300 ease-out"
            />
            {hasFooter && (
              <div
                style={{ opacity: bottomProgress }}
                className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-6 bg-gradient-to-t from-constant-black/6 to-transparent transition-opacity duration-300 ease-out"
              />
            )}
            <div
              ref={scrollRef}
              data-modal-scroll
              onScroll={handleMobileScroll}
              className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
            >
              {content}
            </div>
          </div>
        )}

        {!isLoading && !isMobile && (
          <AutoHideScrollArea
            hasTopShadow
            hasBottomShadow={hasFooter}
            className="flex min-h-0 flex-1 flex-col"
            onScroll={handleScroll}
          >
            {content}
          </AutoHideScrollArea>
        )}

        {showApproveButton && (
          <div className="flex shrink-0 justify-end border-t border-divider bg-background px-4 pt-4 pb-[calc(16px+env(safe-area-inset-bottom))] md:px-5 md:pt-5 md:pb-5">
            <Button
              variant={ButtonVariant.Secondary}
              size={isMobile ? Size.Large : Size.Medium}
              isLoading={isApproving}
              className="max-md:w-full"
              onClick={() => approve()}
            >
              {approveLabel}
            </Button>
          </div>
        )}

        {showApprovedNotice && (
          <div className="flex shrink-0 justify-center border-t border-divider bg-background px-4 pt-4 pb-[calc(16px+env(safe-area-inset-bottom))] md:justify-end md:px-5 md:pt-5 md:pb-5">
            <div className="flex h-12 items-center gap-2 px-4 md:h-10">
              <CheckCircleFilled className="size-4 shrink-0" />
              <p className="text-subhead-14 text-muted-foreground">
                {approveLabel}
              </p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
